import { ParticipantTypes } from "@api/graphql";
import { ERROR_MESSAGES, ValidationErrors, mandatoryFields } from "@pages/register/hooks/errorResolver";
import { UserDetailsInput, ValidationError } from "@pages/register/steps/RegisterFormTypes";

export const accountDetailsErrorResolver = async (
  data: UserDetailsInput,
  isArtist: boolean | undefined,
  isBeneficiary: boolean | undefined,
  isJoiningExistingAccount: boolean | undefined,
  isRoyaltyRecipientOnly: boolean | undefined,
  isInternalUserCreatingAccount?: boolean,
  isAmp?: boolean,
  isCmo?: boolean,
  isAccountActive?: boolean,
  accountStatus?: string,
  participantTypes?: ParticipantTypes[],
): Promise<{ values: UserDetailsInput; errors: ValidationErrors<UserDetailsInput> }> => {
  const errors: ValidationErrors<UserDetailsInput> = {};

  if (
    data.account_details?.year_of_birth &&
    (data.account_details.year_of_birth < 1850 || data.account_details.year_of_birth > new Date().getFullYear())
  ) {
    errors.account_details = {
      ...errors.account_details,
      year_of_birth: {
        type: "pattern",
        message: "Year of birth must be between 1850 and the current year.",
      },
    };
  }

  // yearOfBirth and isIndigenous are optional for account editing according to requirements

  if (
    mandatoryFields.accountDetails.address.address_line_1({
      isInternalUserCreatingAccount: undefined,
      isAccountActive,
      accountStatus,
      participantTypes,
    }) &&
    !data.account_details?.address?.address_line_1
  ) {
    errors.account_details = {
      ...errors.account_details,
      address: {
        ...errors.account_details?.address,
        address_line_1: {
          type: "required",
          message: ERROR_MESSAGES.mandatory,
        } as ValidationError,
      },
    } as ValidationErrors<UserDetailsInput>["account_details"];
  }
  // State validation
  if (
    mandatoryFields.accountDetails.address.state({
      isInternalUserCreatingAccount: undefined,
      isAccountActive,
      accountStatus,
      participantTypes,
    }) &&
    !data.account_details?.address?.state
  ) {
    errors.account_details = {
      ...errors.account_details,
      address: {
        ...errors.account_details?.address,
        state: {
          type: "required",
          message: ERROR_MESSAGES.mandatory,
        } as ValidationError,
      },
    } as ValidationErrors<UserDetailsInput>["account_details"];
  }
  // Suburb/City validation
  if (
    mandatoryFields.accountDetails.address.city({
      isInternalUserCreatingAccount: undefined,
      isAccountActive,
      accountStatus,
      participantTypes,
    }) &&
    !data.account_details?.address?.city
  ) {
    errors.account_details = {
      ...errors.account_details,
      address: {
        ...errors.account_details?.address,
        city: {
          type: "required",
          message: ERROR_MESSAGES.mandatory,
        } as ValidationError,
      },
    } as ValidationErrors<UserDetailsInput>["account_details"];
  }
  // Postcode validation - allow any non-empty string
  const postcode = data.account_details?.address?.postcode;
  const isPostcodeNonEmptyString = String(postcode ?? "").trim() !== "";
  if (
    mandatoryFields.accountDetails.address.postcode({
      isInternalUserCreatingAccount: undefined,
      isAccountActive,
      accountStatus,
      participantTypes,
    }) &&
    !isPostcodeNonEmptyString
  ) {
    errors.account_details = {
      ...errors.account_details,
      address: {
        ...errors.account_details?.address,
        postcode: {
          type: "required",
          message: ERROR_MESSAGES.mandatory,
        } as ValidationError,
      },
    } as ValidationErrors<UserDetailsInput>["account_details"];
  }
  // Country validation
  if (
    mandatoryFields.accountDetails.address.country({
      isInternalUserCreatingAccount: undefined,
      isAccountActive,
      accountStatus,
      participantTypes,
    }) &&
    !data.account_details?.address?.country
  ) {
    errors.account_details = {
      ...errors.account_details,
      address: {
        ...errors.account_details?.address,
        country: {
          type: "required",
          message: ERROR_MESSAGES.mandatory,
        } as ValidationError,
      },
    } as ValidationErrors<UserDetailsInput>["account_details"];
  }
  // Company/Trading name validation
  if (
    mandatoryFields.taxDetails.companyTradingName({
      isArtist,
      isBeneficiary,
      isJoiningExistingAccount,
      accountStatus,
      participantTypes,
    }) &&
    !data.tax_details?.company_trading_name
  ) {
    errors.tax_details = {
      ...errors.tax_details,
      company_trading_name: {
        type: "required",
        message: ERROR_MESSAGES.mandatory,
      },
    };
  }

  // check if should show citizenship fields
  const showCitizenshipFields = !isJoiningExistingAccount && isRoyaltyRecipientOnly;
  if (showCitizenshipFields) {
    if (data.tax_details?.residency_citizenship?.length === 0) {
      errors.tax_details = {
        ...errors.tax_details,
        residency_citizenship: { type: "required", message: ERROR_MESSAGES.mandatory },
      };
    }
  }

  // Residency/Citizenship validation for Artist/Beneficiary
  if (
    mandatoryFields.taxDetails.residencyCitizenship({
      accountStatus,
      participantTypes,
    }) &&
    (!data.tax_details?.residency_citizenship || data.tax_details.residency_citizenship.length === 0)
  ) {
    errors.tax_details = {
      ...errors.tax_details,
      residency_citizenship: { type: "required", message: ERROR_MESSAGES.mandatory },
    };
  }

  return {
    values: data,
    errors,
  };
};
